import { useCallback, useMemo, useRef } from 'react';

import { TUseOptionsParams } from '@/shared/components/ui/form/components/searchable-select/hooks/use-options/use-options.types';

const SELECTED = 'selected';
const UNSELECTED = 'unselected';

export const useOptions = <TOptionShape>({
  value,
  options,
  getItemId,
  disableSort,
}: TUseOptionsParams<TOptionShape>) => {
  // Callers (and DeviceSelect's own callers) commonly pass an inline
  // `getItemId` that gets a new reference on every render. Reading it via a
  // ref keeps `isSelected`'s identity tied only to `value`, so it doesn't
  // itself become a source of new `groupedOptions` array identities.
  const getItemIdRef = useRef(getItemId);
  getItemIdRef.current = getItemId;

  const isSelected = useCallback(
    (val: TOptionShape) => {
      const _getItemId = getItemIdRef.current;

      if (Array.isArray(value)) {
        return value.some(
          (v: TOptionShape) => _getItemId(v) === _getItemId(val),
        );
      }

      if (typeof value === 'string' && typeof val === 'string') {
        return val === value;
      }

      return _getItemId(val) === _getItemId(value);
    },
    [value],
  );

  const groupedOptions = useMemo(() => {
    const optionsWithGroups = options?.map((option) => ({
      ...option,
      group: isSelected(option.value) ? SELECTED : UNSELECTED,
    }));

    if (!optionsWithGroups) {
      return [];
    }

    return [...optionsWithGroups].sort((a, b) => {
      // First sort by group (selected first) - HIGHEST PRIORITY
      if (a.group !== b.group) {
        return a.group === SELECTED ? -1 : 1;
      }

      // Within the same group, handle disabled items (they go to the end of the group)
      if (a.disabled && !b.disabled) return 1;
      if (!a.disabled && b.disabled) return -1;

      // Within the same group and enabled/disabled status, check if options end with wildcard (they go to the top)
      if (typeof a.value === 'string' && typeof b.value === 'string') {
        const aIsWildcard = a.value?.endsWith(':*') || false;
        const bIsWildcard = b.value?.endsWith(':*') || false;

        if (aIsWildcard && !bIsWildcard) return -1;
        if (!aIsWildcard && bIsWildcard) return 1;
      }

      if (disableSort) {
        return 0;
      }

      // Finally, sort alphabetically by label within the same group
      const labelA = a.label?.toLowerCase() || '';
      const labelB = b.label?.toLowerCase() || '';
      return labelA.localeCompare(labelB);
    });
  }, [options, isSelected, disableSort]);

  return {
    groupedOptions,
    isSelected,
  };
};
